import React from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryParams, ArrayParam, withDefault } from 'use-query-params';
import { useQuery } from '@tanstack/react-query';

import { apiResolve, apis } from '@/services';
import { State as FilterState, Action as FilterAction } from '@/controllers/filter-store';
import { useFetchErrorController } from '@/controllers';

import { Params, SearchableParam, SearchableType } from '../types';
import actionCreators from '../stores/query/action-creators';

import * as utils from './utils';

/* NOTE: searchV2: The URL manipulation can be improved drastically with an object always sorting same keys. */

function useUpdateQueryString(
  params: Params,
  addParam: typeof actionCreators['addParam'],
  filterActions: FilterAction,
  filterState: FilterState
) {
  const location = useLocation();

  const urlQueryObject = utils.queryString.parse(location.search, {
    arrayFormat: 'comma',
  });

  const {
    teamPublicIds = [],
    productPublicIds = [],
    capabilityPublicIds = [],
  } = urlQueryObject as Record<SearchableParam, string[]>;

  const { onError } = useFetchErrorController();

  // In phobos-client-ts, some of the native search api methods are broken at
  // the moment. Specifically: searchTeam, searchProduct, searchCapability. This
  // is caused by the "sort" field Type definition issue. e.g. for searchProduct
  // api, the "sort" field of PageProductDTO is defined as an Array<SortObject>,
  // but the actual response is an Object.
  //
  // TODO: Long term wise we should identify the root cause and fix it from
  // phobos generation, but for now, instead of using the searchEntity api
  // methods, we are using the searchEntityRaw api methods to get around this.
  // @see https://hello.jira.atlassian.cloud/browse/TOME-2614
  //
  // The client requires all three id lists; the server treats an empty list as
  // "no filter", so pass [] for the ones not searched by.
  const { isInitialLoading: isLoadingTeams, data: teams } = useQuery(
    ['team', 'search', ...teamPublicIds],
    () =>
      apiResolve(
        apis.team.searchTeamRaw({ teamPublicIds, productPublicIds: [], capabilityPublicIds: [] })
      ),
    {
      onError,
      enabled: teamPublicIds.length > 0,
    }
  );
  const { isInitialLoading: isLoadingProducts, data: products } = useQuery(
    ['product', 'search', ...productPublicIds],
    () =>
      apiResolve(
        apis.product.searchProductRaw({
          productPublicIds,
          teamPublicIds: [],
          capabilityPublicIds: [],
        })
      ),
    {
      onError,
      enabled: productPublicIds.length > 0,
    }
  );
  const { isInitialLoading: isLoadingCapabilities, data: capabilities } = useQuery(
    ['capability', 'search', ...capabilityPublicIds],
    () =>
      apiResolve(
        apis.capability.searchCapabilityRaw({
          capabilityPublicIds,
          teamPublicIds: [],
          productPublicIds: [],
        })
      ),
    {
      onError,
      enabled: capabilityPublicIds.length > 0,
    }
  );

  const addTermsParam = async (scope: SearchableType, response: Response) => {
    try {
      const results = await response?.json();
      if (results.content.length) {
        addParam({
          scope,
          terms: results.content,
        });
      }
    } catch (_) {
      // terms cannot be parsed
    }
  };

  /* NOTE: Listen for the URL params changing: this block adds params to the state */
  React.useEffect(() => {
    if (teamPublicIds.length && teams?.raw) {
      addTermsParam('team', teams.raw);
    }

    if (productPublicIds.length && products?.raw) {
      addTermsParam('product', products.raw);
    }

    if (productPublicIds.length && capabilities?.raw) {
      addTermsParam('capability', capabilities.raw);
    }

    /* NOTE: Set filters */
    const { showDrafts, hasBreachStatusOnly, hasQualityIssuesOnly } = urlQueryObject;

    filterActions.setSLOShowDrafts(showDrafts === 'true');
    filterActions.setSLOHasBreachingOnly(hasBreachStatusOnly === 'true');
    filterActions.setSLOHasQualityIssuesOnly(hasQualityIssuesOnly === 'true');
    /* NOTE: We only care about URL search param change hence */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, teams, products, capabilities]);

  const [, setQuery] = useQueryParams({
    teamPublicIds: withDefault(ArrayParam, []),
    productPublicIds: withDefault(ArrayParam, []),
    capabilityPublicIds: withDefault(ArrayParam, []),
    filters: withDefault(ArrayParam, []),
  });

  /* NOTE: Listen for the URL params changing: this block updates the URL to reflect the state */
  React.useEffect(() => {
    //  Wait until pull state from query params has completed loading before pushing new state
    if (isLoadingTeams || isLoadingProducts || isLoadingCapabilities) {
      return;
    }

    const scopeQueryObject = utils.mapParamsToAPIQueryObject(params);

    setQuery(
      {
        teamPublicIds: scopeQueryObject.teamPublicIds as string[],
        productPublicIds: scopeQueryObject.productPublicIds as string[],
        capabilityPublicIds: scopeQueryObject.capabilityPublicIds as string[],
      },
      'pushIn'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, filterState.slo, isLoadingTeams, isLoadingProducts, isLoadingCapabilities]);
}

export { useUpdateQueryString };
